import { FocusPanel } from "@/components/focus-panel";
import { Form, Head } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import SettingsLayout from "@/layouts/settings_layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FacebookSetup, type FacebookSetupProps } from "./facebook-setup";
import { InstagramSetup, type InstagramSetupProps } from "./instagram-setup";
import { WhatsappSetup, type WhatsappSetupProps } from "./whatsapp-setup";
import { Errors } from "../../requests/controls";

type Integration = {
  id: string;
  name: string;
  category: string;
  documentation: string;
  available: boolean;
  ready: boolean;
  state: string;
  verified_at: string | null;
  fields: { name: string; label: string }[];
  series: string[];
  selected_series: string | null;
};

export default function Integrations({
  integrations,
  facebook,
  instagram,
  whatsapp,
  invoice_default,
  test_connections,
  pending_invoices,
}: {
  integrations: Integration[];
  facebook: FacebookSetupProps;
  instagram: InstagramSetupProps;
  whatsapp: WhatsappSetupProps;
  invoice_default: string;
  test_connections: boolean;
  pending_invoices: number;
}) {
  const { t, i18n } = useTranslation();
  return (
    <SettingsLayout>
      <Head title={t("integrations.title")} />
      <div className="space-y-6">
        <div className="space-y-2">
          <h1 className="text-xl font-semibold">{t("integrations.title")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("integrations.description")}
          </p>
          <p className="text-sm text-muted-foreground">
            {t("access.provider_help")}
          </p>
          {test_connections && (
            <Badge variant="outline">{t("integrations.test")}</Badge>
          )}
        </div>
        <Errors />
        <Card>
          <CardHeader>
            <CardTitle>
              {t("integrations.default", { provider: invoice_default })}
            </CardTitle>
            <CardDescription>{t("integrations.default_help")}</CardDescription>
          </CardHeader>
          {pending_invoices > 0 && (
            <CardContent>
              <p role="alert">
                {t("integrations.pending", { count: pending_invoices })}
              </p>
            </CardContent>
          )}
        </Card>
        <div className="grid items-start gap-4 xl:grid-cols-2">
          {integrations.map((entry) =>
            ["facebook", "whatsapp", "instagram"].includes(entry.id) ? (
              <Card key={entry.id}>
                <CardHeader>
                  <CardTitle>{entry.name}</CardTitle>
                  <CardDescription>
                    {t(`integrations.categories.${entry.category}`)}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <FocusPanel
                    trigger={t("access.configure_provider", {
                      provider: entry.name,
                    })}
                    title={entry.name}
                    description={t("access.provider_help")}
                  >
                    {entry.id === "facebook" ? (
                      <FacebookSetup setup={facebook} />
                    ) : entry.id === "whatsapp" ? (
                      <WhatsappSetup setup={whatsapp} />
                    ) : (
                      <InstagramSetup setup={instagram} />
                    )}
                  </FocusPanel>
                </CardContent>
              </Card>
            ) : (
              <Card key={entry.id}>
                <CardHeader>
                  <CardTitle>{entry.name}</CardTitle>
                  <CardDescription>
                    {t(`integrations.categories.${entry.category}`)}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm font-medium">
                    {t(
                      `integrations.states.${entry.ready ? "ready" : entry.state === "verified" ? "series" : entry.state}`,
                    )}
                  </p>
                  {entry.verified_at && (
                    <p className="text-muted-foreground text-sm">
                      {t("integrations.verified_on", {
                        date: new Intl.DateTimeFormat(i18n.language, {
                          dateStyle: "medium",
                          timeZone: "UTC",
                        }).format(new Date(entry.verified_at)),
                      })}
                    </p>
                  )}
                  {!entry.available && (
                    <p className="text-muted-foreground text-sm">
                      {t("integrations.setup_unavailable")}
                    </p>
                  )}
                  <p className="text-muted-foreground text-sm">
                    {t(`integrations.help.${entry.id}`)}
                  </p>
                  <a
                    className="inline-block text-sm underline underline-offset-4"
                    href={entry.documentation}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("integrations.guide", { provider: entry.name })}
                  </a>
                  {(entry.available || entry.state !== "disconnected") && (
                    <FocusPanel
                      trigger={t("access.configure_provider", {
                        provider: entry.name,
                      })}
                      title={entry.name}
                      description={t(`integrations.help.${entry.id}`)}
                      errorTargets={Object.fromEntries(
                        entry.fields.map((field) => [
                          field.name,
                          `${entry.id}-${field.name}`,
                        ]),
                      )}
                    >
                      {entry.available && entry.fields.length > 0 && (
                        <Form
                          action={`/user/integrations/${entry.id}`}
                          method="patch"
                          resetOnSuccess
                        >
                          {({ processing }) => (
                            <div className="space-y-3">
                              {entry.fields.map((field) => (
                                <div className="space-y-2" key={field.name}>
                                  <Label htmlFor={`${entry.id}-${field.name}`}>
                                    {field.label}
                                  </Label>
                                  <Input
                                    id={`${entry.id}-${field.name}`}
                                    name={`integration[credentials][${field.name}]`}
                                    type="password"
                                    autoComplete="off"
                                    required
                                  />
                                </div>
                              ))}
                              <Button type="submit" disabled={processing}>
                                {t("integrations.connect", {
                                  provider: entry.name,
                                })}
                              </Button>
                            </div>
                          )}
                        </Form>
                      )}
                      {entry.available &&
                        entry.state !== "disconnected" &&
                        entry.series.length > 0 && (
                          <Form
                            action={`/user/integrations/${entry.id}`}
                            method="patch"
                          >
                            {({ processing }) => (
                              <div className="space-y-3">
                                <Label htmlFor={`${entry.id}-series`}>
                                  {t("integrations.series")}
                                </Label>
                                <Select
                                  name="integration[selected_series]"
                                  defaultValue={
                                    entry.selected_series ?? undefined
                                  }
                                >
                                  <SelectTrigger
                                    id={`${entry.id}-series`}
                                    className="w-full"
                                  >
                                    <SelectValue
                                      placeholder={t(
                                        "integrations.choose_series",
                                      )}
                                    />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {entry.series.map((series) => (
                                      <SelectItem key={series} value={series}>
                                        {series}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button type="submit" disabled={processing}>
                                  {t("integrations.save_series")}
                                </Button>
                              </div>
                            )}
                          </Form>
                        )}
                      {!["disconnected"].includes(entry.state) && (
                        <Form
                          action={`/user/integrations/${entry.id}`}
                          method="delete"
                        >
                          {({ processing }) => (
                            <Button
                              type="submit"
                              variant="outline"
                              disabled={processing}
                            >
                              {t("integrations.disconnect", {
                                provider: entry.name,
                              })}
                            </Button>
                          )}
                        </Form>
                      )}
                    </FocusPanel>
                  )}
                </CardContent>
              </Card>
            ),
          )}
        </div>
      </div>
    </SettingsLayout>
  );
}
